/**
 * Secondary-dominant recognition — "Which degree was tonicized?"
 *
 * Plays a host-key cadence (I-IV-V-I) to lock in the home tonic, then a
 * V/x → x → x → I shape so the listener hears the secondary dominant
 * resolve to a non-tonic chord that briefly sounds like a local tonic
 * before returning home. The repeated x is the "partial cadence on the
 * new tonic" the spec calls for — it's what makes x feel landed instead
 * of merely passed through.
 *
 * The user picks which scale degree was tonicized (ii, iii, IV, V, vi).
 * Distractor weighting prefers neighboring degrees because V/V and V/IV
 * share more sonic real estate than V/V and V/iii — the closer the
 * tonicization target is in degree-space, the harder it is to tell
 * apart, and that's the discrimination the exercise should drill.
 *
 * Wrong-answer teach plan: replays V/x → x → V/x → x at half tempo with
 * generous breath between chords, so the chromatic 3rd of the dominant
 * (the "secondary leading tone") and its resolution to the local tonic
 * become unmissable.
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import {
  buildChord,
  degreeOffset,
  DIATONIC_TRIAD_QUALITIES,
  pickKey,
} from "../utils.js";

/** Targets the curriculum supports. ii, iii, IV, V, vi — the five non-tonic
 *  non-diminished diatonic degrees in major. */
const TARGETS: readonly number[] = [2, 3, 4, 5, 6];

const TARGET_LABELS: Record<number, string> = {
  2: "V/ii",
  3: "V/iii",
  4: "V/IV",
  5: "V/V",
  6: "V/vi",
};

const TARGET_SUBLABELS: Record<number, string> = {
  2: "tonicizes ii",
  3: "tonicizes iii",
  4: "tonicizes IV",
  5: "tonicizes V",
  6: "tonicizes vi",
};

export interface SecondaryDominantParams {
  /** Host-key degree being tonicized (2..6). */
  target: number;
}

/**
 * Voice a triad against the previous voicing. Wider MIDI range (36..88)
 * than the shared `smoothVoicing` because V/vi can place the dominant a
 * 4th above its target (tonic + 9 + 7 = +16 semitones), pushing the
 * upper voice past the shared helper's 76 ceiling. Falls back to close
 * root position if no octave permutation fits.
 */
function voiceTriad(prev: number[] | null, notes: number[]): number[] {
  if (!prev) {
    const [root, third, fifth] = notes as [number, number, number];
    const top = fifth < root + 7 ? fifth + 12 : fifth;
    return [root, third, top];
  }
  const candidates: number[][] = [];
  for (const o1 of [-12, 0, 12]) {
    for (const o2 of [-12, 0, 12]) {
      for (const o3 of [-12, 0, 12]) {
        const v = [notes[0]! + o1, notes[1]! + o2, notes[2]! + o3].sort((a, b) => a - b);
        if (v[0]! >= 36 && v[v.length - 1]! <= 88) candidates.push(v);
      }
    }
  }
  if (candidates.length === 0) {
    const [root, third, fifth] = notes as [number, number, number];
    const top = fifth < root + 7 ? fifth + 12 : fifth;
    return [root, third, top];
  }
  let best = candidates[0]!;
  let bestCost = Infinity;
  for (const v of candidates) {
    let cost = 0;
    for (let i = 0; i < v.length; i++) cost += Math.abs(v[i]! - prev[i]!);
    if (cost < bestCost) {
      bestCost = cost;
      best = v;
    }
  }
  return best;
}

/**
 * Voice a chord (3 or 4 notes) with previous-voicing-aware leading.
 * For triads, just delegates to `voiceTriad`. For 4-note chords (dom7
 * secondary dominant), voice the triad portion smoothly and stack the
 * seventh in the octave nearest the top, so the b7 colors the chord
 * without disrupting the lower three voices.
 */
function voiceChord(prev: number[] | null, notes: number[]): number[] {
  if (notes.length === 3) return voiceTriad(prev, notes);
  if (notes.length === 4) {
    const triadVoicing = voiceTriad(prev ? prev.slice(0, 3) : null, notes.slice(0, 3));
    let seventh = notes[3]!;
    const top = triadVoicing[triadVoicing.length - 1]!;
    while (seventh - top > 6) seventh -= 12;
    while (seventh < top) seventh += 12;
    return [...triadVoicing, seventh];
  }
  return [...notes];
}

/** Append one chord (bass + voiced upper) to the builder, advancing the cursor. */
function appendChord(
  builder: AudioPlanBuilder,
  rootMidi: number,
  quality: string,
  prev: number[] | null,
  opts: { dur: number; gap: number; bassGain?: number; chordGain?: number },
): number[] {
  const notes = buildChord(rootMidi, quality);
  const voicing = voiceChord(prev, notes);
  builder.addNote(rootMidi - 12, opts.dur, opts.bassGain ?? 0.11, "bass");
  builder.addChord(voicing, opts.dur, opts.chordGain ?? 0.07, "harmony");
  builder.advance(opts.dur + opts.gap);
  return voicing;
}

/**
 * Pick distractor degrees, weighted toward neighbors of the target.
 * Always includes the two closest by absolute distance; fills any
 * remaining slot from the rest at random. Result is `n` distinct
 * distractor degrees (target excluded).
 */
function pickNeighborDistractors(rng: Rng, target: number, n: number): number[] {
  const others = TARGETS.filter((d) => d !== target);
  // Stable sort by distance ascending — ties keep their original (degree-ascending) order.
  const sorted = [...others].sort((a, b) => Math.abs(a - target) - Math.abs(b - target));
  const closest = sorted.slice(0, Math.min(2, n));
  const rest = sorted.slice(Math.min(2, n));
  const remaining = n - closest.length;
  const filler = remaining > 0 ? rng.sample(rest, remaining) : [];
  return [...closest, ...filler];
}

export function generateSecondaryDominant(
  itemId: string,
  params: SecondaryDominantParams,
  seed: number,
): Exercise {
  const rng = new Rng(seed);
  const key = pickKey(rng, "major");
  const target = params.target;
  if (!TARGETS.includes(target)) {
    throw new Error(`Unsupported tonicization target: ${target}`);
  }

  // Roots in MIDI.
  const targetRootMidi = key.tonicMidi + degreeOffset(target, key.mode);
  const dominantRootMidi = targetRootMidi + 7; // P5 above the local tonic.
  const targetQuality = DIATONIC_TRIAD_QUALITIES[key.mode][target - 1]!;
  const hostQuality = DIATONIC_TRIAD_QUALITIES[key.mode][0]!; // "maj" in ionian.

  const builder = new AudioPlanBuilder();

  // ── Establishing cadence in the host key (I-IV-V-I) ───────────────────
  let prev: number[] | null = null;
  const hostDur = 0.6;
  const hostGap = 0.05;
  for (const deg of [1, 4, 5, 1]) {
    const rootMidi = key.tonicMidi + degreeOffset(deg, key.mode);
    const q = DIATONIC_TRIAD_QUALITIES[key.mode][deg - 1]!;
    prev = appendChord(builder, rootMidi, q, prev, { dur: hostDur, gap: hostGap });
  }
  // Brief breath before the tonicization phrase.
  builder.advance(0.35);

  // ── The tonicization phrase ───────────────────────────────────────────
  // V/x → x → x (sustained as local tonic) → I_host
  const phraseDur = 1.05;
  const phraseGap = 0.06;

  // V/x (dom7)
  prev = appendChord(builder, dominantRootMidi, "dom7", prev, {
    dur: phraseDur,
    gap: phraseGap,
  });
  // x — the local tonic
  prev = appendChord(builder, targetRootMidi, targetQuality, prev, {
    dur: phraseDur * 1.15,
    gap: phraseGap,
  });
  // x repeated to "land" the new tonic — this is the partial cadence
  // shape from spec §4.4.
  prev = appendChord(builder, targetRootMidi, targetQuality, prev, {
    dur: phraseDur,
    gap: phraseGap,
  });
  // I_host — return home so the user knows we're resolving from the
  // tonicization, not living in the new key.
  prev = appendChord(builder, key.tonicMidi, hostQuality, prev, {
    dur: phraseDur * 1.4,
    gap: phraseGap,
  });

  builder.advance(0.5);
  const audio = builder.build();

  // Options: target + 3 distractors, neighbor-weighted.
  const distractorDegrees = pickNeighborDistractors(rng, target, 3);
  const optionDegrees = rng.shuffle([target, ...distractorDegrees]);

  const options: AnswerOption[] = optionDegrees.map((d) => ({
    id: String(d),
    label: TARGET_LABELS[d]!,
    sublabel: TARGET_SUBLABELS[d],
  }));

  return {
    itemId,
    exerciseType: "secondary-dominant-recognition",
    audio,
    prompt: "Which degree was tonicized?",
    options,
    correctAnswerId: String(target),
    meta: {
      keyName: key.name,
      seed,
      explanation: `${TARGET_LABELS[target]} — the secondary dominant resolves to ${TARGET_SUBLABELS[target]!.replace("tonicizes ", "")} in ${key.name}.`,
      distractorCount: options.length - 1,
      teachAudio: buildSecondaryDominantTeachPlan(
        dominantRootMidi,
        targetRootMidi,
        targetQuality,
      ),
    },
  };
}

/**
 * Wrong-answer teach plan: replay V/x → x → V/x → x at half tempo with
 * generous breath between chords. Hearing the secondary dominant land
 * twice on the local tonic makes the tonicization unmissable.
 */
function buildSecondaryDominantTeachPlan(
  dominantRootMidi: number,
  targetRootMidi: number,
  targetQuality: string,
): AudioPlan {
  const builder = new AudioPlanBuilder();
  const dur = 1.7;
  const gap = 0.2;
  let prev: number[] | null = null;
  // First V/x → x with target-voice tag on the local tonic so the engine
  // can highlight the resolution.
  prev = appendChord(builder, dominantRootMidi, "dom7", prev, { dur, gap });
  {
    const notes = buildChord(targetRootMidi, targetQuality);
    const voicing = voiceChord(prev, notes);
    builder.addNote(targetRootMidi - 12, dur, 0.13, "bass");
    builder.addChord(voicing, dur, 0.085, "target");
    builder.advance(dur + gap);
    prev = voicing;
  }
  // Second iteration drives the resolution home.
  prev = appendChord(builder, dominantRootMidi, "dom7", prev, { dur, gap });
  {
    const notes = buildChord(targetRootMidi, targetQuality);
    const voicing = voiceChord(prev, notes);
    builder.addNote(targetRootMidi - 12, dur * 1.2, 0.13, "bass");
    builder.addChord(voicing, dur * 1.2, 0.085, "target");
    builder.advance(dur * 1.2 + gap);
  }
  builder.advance(0.4);
  return builder.build();
}

/** Exposed for tests so they don't have to redo the math. */
export function _secondaryDominantTargets(): readonly number[] {
  return TARGETS;
}
